"use client";

// A guest's enquiry to a business listing. Sent to the business by email; the guest's details go only to them.
import { useState } from "react";

const field = "mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:border-sky-600 focus:outline-none";

export default function EnquiryForm({ placeId, placeName, category }: { placeId: number; placeName: string; category: string }) {
  const [state, setState] = useState<{ kind: "idle" | "sending" | "sent" | "error"; text?: string }>({ kind: "idle" });
  const askGuests = ["restaurants_dining", "events_venues", "tours_experiences", "transport_mobility", "hospitality_services"].includes(category);
  const today = new Date().toISOString().slice(0, 10);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setState({ kind: "sending" });
    try {
      const response = await fetch(`/api/v1/public/business-listings/${placeId}/enquiries`, { method: "POST", body: new FormData(form) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) throw new Error(payload.error || "Could not send your enquiry. Please try again.");
      form.reset();
      setState({ kind: "sent", text: payload.message });
    } catch (problem) {
      setState({ kind: "error", text: problem instanceof Error ? problem.message : "Could not send your enquiry." });
    }
  }

  if (state.kind === "sent") {
    return <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5" role="status"><h2 className="font-semibold">Enquiry sent</h2><p className="mt-2 text-sm text-slate-700">{state.text}</p></section>;
  }
  return (
    <form onSubmit={(event) => void submit(event)} className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="font-semibold">Send an enquiry</h2>
      <p className="mt-1 text-xs text-slate-500">Your details go only to {placeName}, who will reply by email.</p>
      <div className="mt-3 space-y-3">
        <label className="block text-sm">Your name<input name="name" required minLength={2} maxLength={120} autoComplete="name" className={field} /></label>
        <label className="block text-sm">Email<input name="email" type="email" required maxLength={255} autoComplete="email" className={field} /></label>
        <label className="block text-sm">Phone (optional)<input name="phone" type="tel" maxLength={64} autoComplete="tel" placeholder="+250 7…" className={field} /></label>
        {askGuests ? <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">Date (optional)<input name="preferred_date" type="date" min={today} className={field} /></label>
          <label className="block text-sm">Guests (optional)<input name="guests" type="number" min={1} max={5000} inputMode="numeric" className={field} /></label>
        </div> : null}
        <label className="block text-sm">Message<textarea name="message" required minLength={10} maxLength={2000} rows={4} className={field} /></label>
        {/* Robots fill every field; people never see this one. */}
        <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-px w-px opacity-0" />
      </div>
      {state.kind === "error" ? <p role="alert" className="mt-3 text-sm text-red-700">{state.text}</p> : null}
      <button type="submit" disabled={state.kind === "sending"} className="mt-4 min-h-11 w-full rounded-lg bg-sky-700 px-4 font-semibold text-white hover:bg-sky-800 disabled:opacity-60">
        {state.kind === "sending" ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
}
